import { useState, useMemo } from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, Button, Alert, IconButton, InputAdornment, Stack,
    Box, Typography
} from "@mui/material";
import Visibility              from "@mui/icons-material/Visibility";
import VisibilityOff           from "@mui/icons-material/VisibilityOff";
import CheckCircleIcon         from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import { useTranslation } from "react-i18next";
import axios from "../api/axiosConfig";

export default function ChangePasswordDialog({ open, onClose }) {
    const { t } = useTranslation();
    const [form, setForm] = useState({ currentPassword: "", newPassword: "", confirmPassword: "" });
    const [showPwd, setShowPwd] = useState({ current: false, new: false, confirm: false });
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
    const toggle = (field) => setShowPwd(p => ({ ...p, [field]: !p[field] }));

    const pwdReqs = useMemo(() => [
        { label: 'At least 8 characters',          met: form.newPassword.length >= 8 },
        { label: 'One uppercase letter',            met: /[A-Z]/.test(form.newPassword) },
        { label: 'One lowercase letter',            met: /[a-z]/.test(form.newPassword) },
        { label: 'One number',                      met: /[0-9]/.test(form.newPassword) },
        { label: 'One special character (!@#$…)',   met: /[^A-Za-z0-9]/.test(form.newPassword) },
    ], [form.newPassword]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        if (!pwdReqs.every(r => r.met)) {
            setError(t('auth.password_too_short'));
            return;
        }
        if (form.newPassword !== form.confirmPassword) {
            setError(t('auth.passwords_mismatch'));
            return;
        }
        setLoading(true);
        try {
            await axios.post("/api/app_user/change-password", {
                currentPassword: form.currentPassword,
                newPassword: form.newPassword,
            });
            setSuccess(true);
        } catch (err) {
            const code = err.response?.data?.error;
            setError(code === "WRONG_CURRENT_PASSWORD"
                ? t('auth.wrong_current_password')
                : t('auth.change_password_failed'));
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
        setError('');
        setSuccess(false);
        onClose();
    };

    const pwdField = (name, label, showKey) => (
        <TextField fullWidth name={name} label={label} required
                   type={showPwd[showKey] ? "text" : "password"}
                   value={form[name]} onChange={handleChange}
                   slotProps={{
                       input: {
                           endAdornment: (
                               <InputAdornment position="end">
                                   <IconButton onClick={() => toggle(showKey)} edge="end">
                                       {showPwd[showKey] ? <VisibilityOff /> : <Visibility />}
                                   </IconButton>
                               </InputAdornment>
                           )
                       }
                   }} />
    );

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
            <DialogTitle fontWeight={700} sx={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{t('auth.change_password_title')}</DialogTitle>
            <DialogContent>
                {success ? (
                    <Alert severity="success" sx={{ mt: 1 }}>{t('auth.password_changed_success')}</Alert>
                ) : (
                    <Stack spacing={2} mt={1} component="form" id="change-pwd-form" onSubmit={handleSubmit}>
                        {pwdField("currentPassword", t('auth.current_password'), "current")}
                        {pwdField("newPassword",     t('auth.new_password'),     "new")}
                        {form.newPassword.length > 0 && (
                            <Box sx={{ pl: 0.5 }}>
                                {pwdReqs.map(req => (
                                    <Stack key={req.label} direction="row" spacing={0.8} alignItems="center" sx={{ mb: 0.4 }}>
                                        {req.met
                                            ? <CheckCircleIcon sx={{ fontSize: '0.95rem', color: '#4A6741' }} />
                                            : <RadioButtonUncheckedIcon sx={{ fontSize: '0.95rem', color: 'text.disabled' }} />}
                                        <Typography variant="caption"
                                                    sx={{ color: req.met ? '#2C1F14' : 'text.disabled', fontWeight: req.met ? 600 : 400 }}>
                                            {req.label}
                                        </Typography>
                                    </Stack>
                                ))}
                            </Box>
                        )}
                        {pwdField("confirmPassword", t('auth.confirm_password'), "confirm")}
                        {error && <Alert severity="error">{error}</Alert>}
                    </Stack>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={handleClose}>{success ? t('common.close') : t('common.cancel')}</Button>
                {!success && (
                    <Button type="submit" form="change-pwd-form" variant="contained"
                            disabled={loading}
                            sx={{ bgcolor: "#C4622D", "&:hover": { bgcolor: "#9A4A1F" } }}>
                        {loading ? t('common.saving') : t('auth.change_password_btn')}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
}